import MeterBar from '@/components/console/meter-bar';
import StatusPill from '@/components/console/status-pill';
import EmptyState from '@/components/empty-state';
import PageHeader from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import SetupStatusPill from '@/pages/apps/components/setup-status-pill';
import { app_path, billing_path, new_app_path } from '@/rails/routes';
import type { MeterRow, PlanSummary, ProvisioningStatus } from '@/types/platform';
import { Link } from '@inertiajs/react';

interface AppCard {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  createdAt: string;
  templateName: string | null;
  listCount: number;
  provisioningStatus: ProvisioningStatus;
  published: boolean;
  publicUrl: string | null;
}

interface AppsIndexProps {
  apps: AppCard[];
  meter: MeterRow[];
  plan: PlanSummary;
  canAddApp: boolean;
}

function plural(count: number, word: string): string {
  return `${count} ${count === 1 ? word : `${word}s`}`;
}

const AppsIndex = ({ apps, meter, plan, canAddApp }: AppsIndexProps) => {
  const newAppButton = canAddApp ? (
    <Button asChild>
      <Link href={new_app_path()}>New app</Link>
    </Button>
  ) : (
    <Button disabled>New app</Button>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Apps"
        description="Each app keeps its own lists, attachments, invoices, and messages."
        actions={newAppButton}
      />

      {canAddApp ? null : (
        <p className="text-muted-foreground text-sm">
          Your {plan.name} plan includes {plural(plan.appLimit, 'app')}.{' '}
          <Link href={billing_path()} className="text-foreground font-medium underline">
            See plans
          </Link>
        </p>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2">
          {apps.length === 0 ? (
            <Card>
              <CardContent>
                <EmptyState
                  title="No apps yet"
                  description="Start from a template or describe what you need. Your first app takes a minute to set up."
                  action={canAddApp ? newAppButton : undefined}
                />
              </CardContent>
            </Card>
          ) : (
            <ul className="grid gap-4 sm:grid-cols-2">
              {apps.map((app) => (
                <li key={app.id} className="min-w-0">
                  <AppCardItem app={app} />
                </li>
              ))}
            </ul>
          )}
        </div>

        <Card className="min-w-0 self-start">
          <CardHeader>
            <CardTitle>
              <h2>{plan.name} plan usage</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {meter.map((row) => (
              <MeterBar key={row.key} row={row} />
            ))}
            <Link href={billing_path()} className="inline-block text-sm font-medium underline">
              Plan and usage
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const AppCardItem = ({ app }: { app: AppCard }) => {
  return (
    <Card className="hover:bg-muted/40 relative h-full gap-3 transition-colors">
      <CardHeader>
        <CardTitle>
          <h2 className="truncate">
            <Link
              href={app_path(app.id)}
              className="focus-visible:after:ring-ring after:absolute after:inset-0 after:rounded-xl focus-visible:outline-none focus-visible:after:ring-2"
            >
              {app.name}
            </Link>
          </h2>
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <SetupStatusPill status={app.provisioningStatus} />
          {app.published ? <StatusPill tone="success">Published</StatusPill> : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {app.description ? (
          <p className="text-muted-foreground line-clamp-2">{app.description}</p>
        ) : null}
        <p className="text-muted-foreground">
          {plural(app.listCount, 'list')}
          {app.templateName ? ` · ${app.templateName} template` : ''}
        </p>
        {app.published && app.publicUrl ? (
          <a
            href={app.publicUrl}
            target="_blank"
            rel="noreferrer"
            className="relative z-10 block truncate font-medium underline"
          >
            {app.publicUrl}
          </a>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default AppsIndex;
